import { useState, useCallback } from 'react';
import { useApi } from '@/hooks/useApi';
import { apiFetch } from '@/lib/api';
import {
  ClipboardCheck,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Plus,
  Loader2,
  X,
} from 'lucide-react';

interface Inspection {
  id: string;
  vehicle_id: string;
  vehicle_name: string;
  inspection_type: string;
  inspection_date: string;
  status: 'pending' | 'passed' | 'failed' | 'requires_attention';
  inspector_name?: string;
  expiry_date?: string;
  notes?: string;
}

interface InspectionsResponse {
  inspections: Inspection[];
  data: Inspection[];
  metrics: {
    total: number;
    passed: number;
    failed: number;
    expiring_soon: number;
  };
}

interface VehicleOption { id: string; name: string }

const INSPECTION_TYPES = [
  { value: 'pre_trip', label: 'Pre-Trip' },
  { value: 'post_trip', label: 'Post-Trip' },
  { value: 'dot_annual', label: 'DOT Annual' },
  { value: 'dot_periodic', label: 'DOT Periodic' },
  { value: 'safety', label: 'Safety' },
  { value: 'custom', label: 'Custom' },
];

const INSPECTION_STATUSES = [
  { value: 'pending', label: 'Pending' },
  { value: 'passed', label: 'Passed' },
  { value: 'failed', label: 'Failed' },
  { value: 'requires_attention', label: 'Requires Attention' },
];

const statusColors: Record<string, string> = {
  pending: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  passed: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  failed: 'bg-red-500/20 text-red-400 border-red-500/30',
  requires_attention: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
};

const statusLabels: Record<string, string> = {
  pending: 'Pending',
  passed: 'Passed',
  failed: 'Failed',
  requires_attention: 'Attention',
};

export default function InspectionsPage() {
  const { data, isLoading, refetch } = useApi<InspectionsResponse>('/api/inspections');
  const { data: vehiclesData } = useApi<{ vehicles: VehicleOption[] }>('/api/vehicles?pageSize=100');
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const metrics = data?.metrics ?? { total: 0, passed: 0, failed: 0, expiring_soon: 0 };
  const inspections = data?.inspections ?? data?.data ?? [];
  const vehicles = vehiclesData?.vehicles ?? [];

  const metricCards = [
    { label: 'Total Inspections', value: metrics.total, icon: ClipboardCheck, color: '#10B981' },
    { label: 'Passed', value: metrics.passed, icon: CheckCircle2, color: '#10B981' },
    { label: 'Failed', value: metrics.failed, icon: XCircle, color: '#EF4444' },
    { label: 'Expiring Soon', value: metrics.expiring_soon, icon: AlertTriangle, color: '#EAB308' },
  ];

  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);
    setFormError('');
    const fd = new FormData(e.currentTarget);
    const selectedVehicle = vehicles.find(v => v.id === fd.get('vehicle_id'));

    try {
      await apiFetch('/api/inspections', {
        method: 'POST',
        body: JSON.stringify({
          vehicle_id: fd.get('vehicle_id'),
          vehicle_name: selectedVehicle?.name ?? '',
          inspection_type: fd.get('inspection_type'),
          status: fd.get('status') || 'pending',
          inspection_date: fd.get('inspection_date'),
          expiry_date: fd.get('expiry_date') || undefined,
          inspector_name: fd.get('inspector_name') || undefined,
          notes: fd.get('notes') || undefined,
        }),
      });
      setShowForm(false);
      refetch();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to create inspection');
    } finally {
      setSaving(false);
    }
  }, [vehicles, refetch]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg" style={{ background: 'rgba(16,185,129,0.1)' }}>
            <ClipboardCheck className="w-6 h-6" style={{ color: '#10B981' }} />
          </div>
          <div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--foreground)' }}>Inspections</h1>
            <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Vehicle inspection records and scheduling</p>
          </div>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-foreground transition-colors"
          style={{ background: '#10B981' }}
        >
          <Plus className="h-4 w-4" />
          New Inspection
        </button>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {metricCards.map((m) => (
          <div key={m.label} className="rounded-xl p-5" style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }}>
            <div className="flex items-center gap-2 mb-2">
              <m.icon className="w-4 h-4" style={{ color: m.color }} />
              <span className="text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--muted-foreground)' }}>{m.label}</span>
            </div>
            <span className="text-2xl font-bold" style={{ color: m.color }}>{m.value}</span>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="rounded-xl overflow-hidden" style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }}>
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--muted-foreground)' }} />
          </div>
        ) : inspections.length === 0 ? (
          <div className="text-center py-16" style={{ color: 'var(--muted-foreground)' }}>
            <ClipboardCheck className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No inspections found</p>
            <p className="text-xs mt-1">Create your first inspection to get started.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b" style={{ borderColor: 'var(--glass-border)' }}>
                  {['Vehicle', 'Type', 'Date', 'Status', 'Inspector', 'Expiry'].map(h => (
                    <th key={h} className="text-left text-xs font-medium uppercase tracking-wide px-4 py-3" style={{ color: 'var(--muted-foreground)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {inspections.map((item) => (
                  <tr key={item.id} className="border-b hover:bg-white/5 transition-colors" style={{ borderColor: 'var(--glass-border)' }}>
                    <td className="px-4 py-3 font-medium" style={{ color: 'var(--foreground)' }}>{item.vehicle_name}</td>
                    <td className="px-4 py-3 capitalize" style={{ color: 'var(--foreground)' }}>{(item.inspection_type || '').replace(/_/g, ' ')}</td>
                    <td className="px-4 py-3" style={{ color: 'var(--muted-foreground)' }}>{item.inspection_date ? new Date(item.inspection_date).toLocaleDateString() : '--'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${statusColors[item.status] ?? ''}`}>
                        {statusLabels[item.status] ?? item.status}
                      </span>
                    </td>
                    <td className="px-4 py-3" style={{ color: 'var(--muted-foreground)' }}>{item.inspector_name ?? '--'}</td>
                    <td className="px-4 py-3" style={{ color: 'var(--muted-foreground)' }}>{item.expiry_date ? new Date(item.expiry_date).toLocaleDateString() : '--'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Inspection Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)' }}>
          <div className="w-full max-w-lg rounded-xl p-6 max-h-[90vh] overflow-y-auto"
            style={{ background: 'var(--surface-1)', border: '1px solid var(--border-subtle)' }}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold" style={{ color: 'var(--foreground)' }}>New Inspection</h2>
              <button onClick={() => setShowForm(false)} className="p-1 rounded hover:bg-white/10">
                <X className="w-4 h-4" style={{ color: 'var(--muted-foreground)' }} />
              </button>
            </div>
            {formError && <div className="mb-4 p-3 rounded-lg text-sm" style={{ background: 'rgba(239,68,68,0.1)', color: '#EF4444' }}>{formError}</div>}
            <form onSubmit={handleSubmit} className="space-y-4">
              <FormField label="Vehicle" required>
                <select name="vehicle_id" required className="form-select">
                  <option value="">Select vehicle...</option>
                  {vehicles.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </FormField>
              <FormField label="Inspection Type" required>
                <select name="inspection_type" required className="form-select">
                  {INSPECTION_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </FormField>
              <FormField label="Status">
                <select name="status" className="form-select" defaultValue="pending">
                  {INSPECTION_STATUSES.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </FormField>
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Inspection Date" required>
                  <input type="date" name="inspection_date" required className="form-input" />
                </FormField>
                <FormField label="Expiry Date">
                  <input type="date" name="expiry_date" className="form-input" />
                </FormField>
              </div>
              <FormField label="Inspector Name">
                <input type="text" name="inspector_name" className="form-input" placeholder="Inspector name" />
              </FormField>
              <FormField label="Notes">
                <textarea name="notes" rows={2} className="form-input" placeholder="Inspection notes..." />
              </FormField>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)}
                  className="px-4 py-2 text-sm rounded-lg" style={{ color: 'var(--muted-foreground)' }}>Cancel</button>
                <button type="submit" disabled={saving}
                  className="px-4 py-2 text-sm font-medium text-foreground rounded-lg flex items-center gap-2"
                  style={{ background: '#10B981', opacity: saving ? 0.7 : 1 }}>
                  {saving && <Loader2 className="w-3 h-3 animate-spin" />}
                  Create Inspection
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function FormField({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1" style={{ color: 'var(--foreground)' }}>
        {label}{required && <span style={{ color: '#EF4444' }}> *</span>}
      </label>
      {children}
    </div>
  );
}
